import type { Todo } from '@/api'

export interface TodoNode {
  todo: Todo
  children: TodoNode[]
}

// Subtasks via RELATED-TO (parent_uid). Todos whose parent isn't in the list (deleted, or in
// another calendar) become top-level; RELATED-TO cycles -- which do exist in the wild -- are
// broken at the first repeated node instead of looping.
export function buildTree(todos: Todo[]): TodoNode[] {
  const nodes = new Map<string, TodoNode>()
  for (const todo of todos) nodes.set(todo.uid, { todo, children: [] })

  const roots: TodoNode[] = []
  for (const node of nodes.values()) {
    const parent = node.todo.parent_uid ? nodes.get(node.todo.parent_uid) : undefined
    if (parent && parent !== node && !isAncestor(node, parent, nodes)) {
      parent.children.push(node)
    } else {
      roots.push(node)
    }
  }
  return roots
}

function isAncestor(candidate: TodoNode, of: TodoNode, nodes: Map<string, TodoNode>): boolean {
  const seen = new Set<string>()
  let current: TodoNode | undefined = of
  while (current && !seen.has(current.todo.uid)) {
    if (current === candidate) return true
    seen.add(current.todo.uid)
    current = current.todo.parent_uid ? nodes.get(current.todo.parent_uid) : undefined
  }
  return false
}

export function descendantUids(todos: Todo[], uid: string): string[] {
  const childrenByParent = new Map<string, string[]>()
  for (const todo of todos) {
    if (!todo.parent_uid) continue
    const list = childrenByParent.get(todo.parent_uid) ?? []
    list.push(todo.uid)
    childrenByParent.set(todo.parent_uid, list)
  }
  const result: string[] = []
  const seen = new Set([uid])
  const stack = [...(childrenByParent.get(uid) ?? [])]
  while (stack.length) {
    const next = stack.pop()!
    if (seen.has(next)) continue
    seen.add(next)
    result.push(next)
    stack.push(...(childrenByParent.get(next) ?? []))
  }
  return result
}

export function countNodes(nodes: TodoNode[], predicate: (todo: Todo) => boolean): number {
  return nodes.reduce(
    (sum, n) => sum + (predicate(n.todo) ? 1 : 0) + countNodes(n.children, predicate),
    0,
  )
}
